import { useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getDownloadURL, getStorage, ref, uploadBytesResumable } from 'firebase/storage'
import { firebaseApp } from '../../lib/firebase'
import { api } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { formatDate } from '../../lib/format'
import type { KycDoc, ListResponse } from '../../types'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Input, Select,
  Skeleton, StatusBadge,
} from '../../components/ui'
import { CloseIcon, DocIcon, IdIcon, PlusIcon } from '../../components/icons'

const DOC_TYPES = ['National ID', "Driver's License", 'Passport', 'Proof of Address', 'Other']
const MAX_BYTES = 10 * 1024 * 1024

export default function Kyc() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [progress, setProgress] = useState<number | null>(null)
  const [uploadError, setUploadError] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['miner', 'kyc'],
    queryFn: () => api.get<ListResponse<KycDoc>>('/miner/kyc'),
  })

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/miner/kyc', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['miner', 'kyc'] })
      void qc.invalidateQueries({ queryKey: ['miner', 'dashboard'] })
      setShowForm(false)
      setProgress(null)
    },
  })

  /* The API stores a URL, not the bytes - so the file goes to Firebase Storage
   * first and only the resulting download URL is sent to Flask. */
  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setUploadError('')
    const form = new FormData(e.currentTarget)
    const docType = String(form.get('doc_type') ?? '')
    const file = fileRef.current?.files?.[0]

    if (!file) { setUploadError('Choose a file to upload.'); return }
    if (file.size > MAX_BYTES) {
      setUploadError('That file is larger than 10 MB. Please upload a smaller scan or photo.')
      return
    }
    if (!user?.uid) { setUploadError('Your session expired. Sign in again.'); return }

    const ext = file.name.includes('.') ? file.name.split('.').pop() : 'bin'
    const path = `kyc_docs/${user.uid}/${crypto.randomUUID()}.${ext}`
    const task = uploadBytesResumable(ref(getStorage(firebaseApp), path), file, {
      contentType: file.type || 'application/octet-stream',
    })

    setProgress(0)
    task.on('state_changed',
      (snap) => setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      (err) => {
        setProgress(null)
        setUploadError(
          err.code === 'storage/unauthorized'
            ? 'You do not have permission to upload. Contact your administrator.'
            : `Upload failed: ${err.message}`,
        )
      },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref)
        save.mutate({ doc_type: docType, title: file.name, url })
      },
    )
  }

  const items = data?.items ?? []

  return (
    <>
      <div className="page-head">
        <div>
          <h1>My documents</h1>
          <p className="page-head__sub">
            Identity documents used to verify your account.
          </p>
        </div>
        <Button className="fab-trigger__inline" icon={<PlusIcon />} onClick={() => setShowForm((s) => !s)}>
          {showForm ? 'Cancel' : 'Upload document'}
        </Button>
        <button
          type="button" className="fab"
          onClick={() => setShowForm((s) => !s)}
          aria-label={showForm ? 'Cancel' : 'Upload document'}
        >
          {showForm ? <CloseIcon size={22} /> : <PlusIcon size={22} />}
        </button>
      </div>

      {showForm && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="Upload a document"
                      sub="Accepted: photo or scan, up to 10 MB. Reviewed by your cooperative leader." />
          <CardBody>
            <form onSubmit={onSubmit}>
              <div className="stack">
                {uploadError && <Alert tone="danger">{uploadError}</Alert>}
                {save.isError && <Alert tone="danger">{(save.error as Error).message}</Alert>}

                <Select label="Document type" name="doc_type" required defaultValue="">
                  <option value="" disabled>Select a type…</option>
                  {DOC_TYPES.map((d) => <option key={d} value={d}>{d}</option>)}
                </Select>

                <Input label="File" type="file" ref={fileRef}
                       accept="image/*,application/pdf" required
                       hint="A clear photo of the whole document, or a PDF scan." />

                {progress !== null && (
                  <div>
                    <div className="onboard__bar"><div className="onboard__fill" style={{ width: `${progress}%` }} /></div>
                    <div className="field__hint" style={{ marginTop: 6 }}>
                      {progress < 100 ? `Uploading… ${progress}%` : 'Finalising…'}
                    </div>
                  </div>
                )}

                <div className="row">
                  <Button type="submit" loading={progress !== null || save.isPending}>
                    Upload
                  </Button>
                  <Button type="button" variant="ghost"
                          onClick={() => { setShowForm(false); setProgress(null); setUploadError('') }}>
                    Cancel
                  </Button>
                </div>
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader title="Submitted documents" />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}>
              <Alert tone="danger">{(error as Error).message}</Alert>
            </div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack">
              <Skeleton h={20} /><Skeleton h={20} w="70%" />
            </div>
          ) : items.length === 0 ? (
            <EmptyState icon={<IdIcon size={22} />} title="No documents yet"
                        body="Upload a national ID, passport or driver's licence to verify your identity." />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Type</th><th>File</th><th>Uploaded</th><th>Status</th><th /></tr>
                </thead>
                <tbody>
                  {items.map((d, i) => {
                    const href = d.url || d.file_url
                    return (
                      <tr key={d.id ?? i}>
                        <td>{d.doc_type || '—'}</td>
                        <td>{d.title || d.file_name || '—'}</td>
                        <td>{formatDate(d.uploaded_at)}</td>
                        <td><StatusBadge status={d.status} /></td>
                        <td className="table__num">
                          {href && (
                            <a href={href} target="_blank" rel="noreferrer"
                               style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--text-sm)' }}>
                              <DocIcon size={15} /> View
                            </a>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}
